import React, { useState, useMemo } from 'react';
import { Sword, Heart, TrendingUp } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { getChampionDeepDive } from '../lib/analytics';
import { getChampionIconUrl } from '../lib/champions';
import { SectionHeader, Card, ChartTooltip, MetricTile, CHART_COLORS } from './analytics/shared';

/**
 * APEX v8.10 — ChampionsDataView (refonte)
 *
 * Repris de l'UX ChampionDeepDiveSection (Analytics) :
 *   - Selector compact des champions joués
 *   - Header champion avec gros visuel
 *   - KPIs en tuiles
 *   - Courbe de progression
 *   - Matchups ADC + Synergies Support côte à côte (enrichis Lane WR @15 + KDA)
 *
 * Heatmap et morts coûteuses retirées (présentes dans Analytics > Morts).
 */

function MatchupRow({ entry, version, champions, hoverColor = 'cyan-400' }) {
  const champ = champions?.find(c => c.name === entry.name || c.id === entry.name);
  const wrColor = entry.winrate >= 60 ? 'text-cyan-400'
    : entry.winrate >= 50 ? 'text-emerald-400'
    : entry.winrate >= 40 ? 'text-amber-400'
    : 'text-rose-400';
  const laneColor = entry.laneWr == null ? 'text-zinc-600'
    : entry.laneWr >= 55 ? 'text-cyan-400'
    : entry.laneWr >= 45 ? 'text-emerald-400'
    : entry.laneWr >= 35 ? 'text-amber-400'
    : 'text-rose-400';

  return (
    <div className={`grid grid-cols-[auto_1fr_auto_auto_auto] gap-3 items-center py-1.5 border-l-2 border-zinc-800 hover:border-${hoverColor}/50 pl-3 transition`}>
      {champ
        ? <img src={getChampionIconUrl(champ.id, version)} alt="" className="w-7 h-7 object-cover" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        : <div className="w-7 h-7" />}
      <div className="min-w-0">
        <div className="font-mono text-sm text-zinc-300 truncate">{entry.name}</div>
        <div className="text-[9px] text-zinc-600 font-mono">{entry.games}G · KDA {entry.avgKda ?? '—'}</div>
      </div>
      <div className="text-right">
        <div className="text-[9px] uppercase tracking-widest text-zinc-600 font-bold">Lane@15</div>
        <div className={`text-xs font-mono font-bold ${laneColor}`}>
          {entry.laneWr != null ? `${entry.laneWr}%` : '—'}
        </div>
      </div>
      <div className="text-right pl-2">
        <div className="text-[9px] uppercase tracking-widest text-zinc-600 font-bold">WR</div>
        <div className={`font-mono text-sm font-bold ${wrColor}`}>{entry.winrate}%</div>
      </div>
    </div>
  );
}

export default function ChampionsDataView({ games, champions, version }) {
  const playedChampions = useMemo(() => {
    const set = new Set((games || []).map(g => g.champion).filter(Boolean));
    return Array.from(set).map(name => {
      const champGames = games.filter(g => g.champion === name);
      const wins = champGames.filter(g => g.result === 'W').length;
      return {
        name,
        count: champGames.length,
        winrate: Math.round(wins / champGames.length * 100)
      };
    }).sort((a, b) => b.count - a.count);
  }, [games]);

  const [selected, setSelected] = useState(playedChampions[0]?.name || null);
  const data = useMemo(() => selected ? getChampionDeepDive(games, selected) : null, [games, selected]);

  if (playedChampions.length === 0) {
    return (
      <div className="border border-dashed border-zinc-800 p-8 text-center text-zinc-500 text-sm">
        Pas de données de champion
      </div>
    );
  }

  const selectedChamp = champions?.find(c => c.name === selected || c.id === selected);

  return (
    <div className="space-y-6">

      {/* Selector — barre de champions joués */}
      <div>
        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-semibold mb-3">
          Sélectionne un champion
        </div>
        <div className="flex flex-wrap gap-2">
          {playedChampions.map(c => {
            const isSelected = selected === c.name;
            const champ = champions?.find(ch => ch.name === c.name || ch.id === c.name);
            return (
              <button
                key={c.name}
                onClick={() => setSelected(c.name)}
                className={`flex items-center gap-2 border px-3 py-2 transition ${
                  isSelected
                    ? 'border-cyan-400 bg-cyan-400/10'
                    : 'border-zinc-800 hover:border-zinc-700 bg-zinc-950'
                }`}
              >
                {champ && (
                  <img
                    src={getChampionIconUrl(champ.id, version)}
                    alt=""
                    className="w-8 h-8 object-cover"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                )}
                <div className="text-left">
                  <div className={`font-bold text-sm ${isSelected ? 'text-white' : 'text-zinc-300'}`}>
                    {c.name}
                  </div>
                  <div className="text-[10px] font-mono text-zinc-500">
                    {c.count}G · {c.winrate}%WR
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {data && (
        <>
          {/* Header champion */}
          <div className="border border-cyan-400/30 bg-gradient-to-br from-cyan-400/5 to-fuchsia-400/5 p-6 flex items-center gap-5">
            {selectedChamp && (
              <img
                src={getChampionIconUrl(selectedChamp.id, version)}
                alt=""
                className="w-20 h-20 object-cover border-2 border-cyan-400/50"
                style={{ boxShadow: '0 0 20px rgba(34, 211, 238, 0.3)' }}
              />
            )}
            <div className="flex-1">
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-semibold mb-1">
                Stats globales
              </div>
              <div className="text-4xl font-black text-white">{data.name}</div>
              <div className="flex items-center gap-4 mt-2 font-mono text-sm">
                <span className="text-zinc-400">{data.games} games</span>
                <span className={data.winrate >= 54 ? 'text-cyan-400' : 'text-rose-400'}>
                  {data.winrate}% WR
                </span>
                <span className="text-zinc-400">{data.wins}W / {data.losses}L</span>
              </div>
            </div>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricTile label="KDA moyen"     value={data.avgKda} color="cyan" />
            <MetricTile label="Kills / game"  value={data.avgKills} color="white" />
            <MetricTile label="Assists / game" value={data.avgAssists} color="white" />
            <MetricTile label="Morts / game"  value={data.avgDeaths} color={data.avgDeaths <= 4 ? 'cyan' : 'rose'} />
            {data.avgDmgPerMin > 0 && <MetricTile label="DMG / min" value={data.avgDmgPerMin.toLocaleString()} color="fuchsia" />}
            {data.avgDmgShare && <MetricTile label="Damage share" value={data.avgDmgShare} unit="%" color="fuchsia" />}
          </div>

          {/* Progression */}
          {data.progression.length >= 3 && (
            <Card>
              <SectionHeader
                title={`Progression sur ${data.name}`}
                subtitle="Winrate rolling 5 games"
                icon={TrendingUp}
              />
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={data.progression}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="gameNumber" stroke="#71717a" style={{ fontSize: 11, fontFamily: 'monospace' }} />
                  <YAxis stroke="#71717a" style={{ fontSize: 11, fontFamily: 'monospace' }} domain={[0, 100]} />
                  <Tooltip content={<ChartTooltip />} />
                  <ReferenceLine y={54} stroke={CHART_COLORS.fuchsia} strokeDasharray="4 4" />
                  <Line
                    type="monotone"
                    dataKey="winrate"
                    stroke={CHART_COLORS.cyan}
                    strokeWidth={2}
                    dot={{ fill: CHART_COLORS.cyan, r: 3 }}
                    name="Winrate %"
                  />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          )}

          {/* Matchups ADC + Synergies Support — côte à côte */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <SectionHeader
                title="Matchups ADC"
                subtitle={`Adversaires face à ${data.name}`}
                icon={Sword}
              />
              <div className="space-y-1">
                {data.matchups.length > 0 ? data.matchups.map(m => (
                  <MatchupRow key={m.name} entry={m} champions={champions} version={version} hoverColor="cyan-400" />
                )) : (
                  <div className="text-xs text-zinc-600 italic">Pas de matchup enregistré.</div>
                )}
              </div>
            </Card>

            <Card>
              <SectionHeader
                title="Synergies Support"
                subtitle={`Supports joués avec ${data.name}`}
                icon={Heart}
              />
              <div className="space-y-1">
                {data.synergies.length > 0 ? data.synergies.map(s => (
                  <MatchupRow key={s.name} entry={s} champions={champions} version={version} hoverColor="fuchsia-400" />
                )) : (
                  <div className="text-xs text-zinc-600 italic">Pas de synergie enregistrée.</div>
                )}
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
